import * as React from 'react';

import { ChatUnreadMessagesInfo } from '../../../components/ChatUnreadMessagesInfo';
import { SystemInfoBubble } from '../../../components/SystemInfoBubble';
import { TMessage, TOutcomingStatus } from '../../../types/messages';
import { IParsedMessage } from '../../../utils/messageUtils';
import { ChatMessageContainer } from '../../ChatMessageContainer';

let hasChatUnreadMessagesInfo = false;

export const useChatPageMessages = (
  messages: IParsedMessage[],
  setRefInMessagesMap: (r: HTMLDivElement | null, message: TMessage, messageId: string) => void,
  unreadRef: React.RefObject<HTMLDivElement>,
): { messagesNode: React.ReactNode } => {
  const indexFirstUnreadMessageRef = React.useRef<number | null>(null);

  const messagesNode = React.useMemo(() => {
    const nodes = messages.map(({ message, messageId }, index) => {
      const nextMessage = messages[index + 1];
      const previousMessage = messages[index - 1];

      const isFirstUserPhrase = !previousMessage || previousMessage?.message?.userId !== message.userId;
      const isLastInChain =
        !nextMessage ||
        (nextMessage.message.type !== message.type && nextMessage.message.type !== 'offer') ||
        nextMessage.message.userId !== message.userId ||
        nextMessage.message.fromChatBot !== message.fromChatBot;
      const isOutcoming = message.isOutcoming;
      const isUnread = !isOutcoming && !message.isRead;

      const isOfferGallery = (message.offers?.length ?? 0) > 1;

      if (isUnread && indexFirstUnreadMessageRef.current === null) {
        indexFirstUnreadMessageRef.current = index;
      }

      if (message.type === 'systemInfo') {
        return <SystemInfoBubble key={messageId} text={message.message} />;
      }

      return (
        <div ref={r => setRefInMessagesMap(r, message, messageId)} key={messageId}>
          <ChatMessageContainer
            message={message as TMessage & { status: TOutcomingStatus }}
            messageId={messageId}
            isLastInChain={isLastInChain}
            isFirstUserPhrase={isFirstUserPhrase}
            isOfferGallery={isOfferGallery}
          />
        </div>
      );
    });

    return nodes;
  }, [messages, setRefInMessagesMap]);

  React.useEffect(() => {
    indexFirstUnreadMessageRef.current = null;
    hasChatUnreadMessagesInfo = false;
  }, [messages]);

  if (indexFirstUnreadMessageRef.current !== null && messagesNode && !hasChatUnreadMessagesInfo) {
    const pureMessagesNode = messagesNode.slice();

    pureMessagesNode.splice(indexFirstUnreadMessageRef.current, 0, <ChatUnreadMessagesInfo unreadRef={unreadRef} />);
    hasChatUnreadMessagesInfo = true;

    return { messagesNode: pureMessagesNode };
  }

  return { messagesNode };
};
